<ra-modal
    [title]="'order.iaWizard.title' | translate"
    size="xlarge"
    [disabled]="creating()"
    (close)="requestClose()">

    <div class="wizard">

        <!-- Progresso -->
        <div class="wizard__progress" aria-hidden="true">
            @for (s of steps(); track s; let i = $index) {
                <span class="wizard__bar" [class.wizard__bar--done]="i <= stepIndex()"></span>
            }
        </div>
        <p class="wizard__step-label">
            {{ 'order.iaWizard.stepOf' | translate: { current: stepIndex() + 1, total: steps().length } }}
        </p>

        <!-- ───────────── Passo 1: tipo ───────────── -->
        @if (step() === 'type') {
            <div class="wizard__head">
                <h3>{{ 'order.iaWizard.type.title' | translate }}</h3>
                <p>{{ 'order.iaWizard.type.subtitle' | translate }}</p>
            </div>

            <div class="type-grid" role="radiogroup" [attr.aria-label]="'order.iaWizard.type.title' | translate">
                @for (option of typeOptions; track option.type) {
                    <button
                        type="button"
                        role="radio"
                        class="type-card"
                        [attr.aria-checked]="type() === option.type"
                        [class.type-card--selected]="type() === option.type"
                        [style.--card-accent]="option.accent"
                        [style.--card-accent-light]="option.accentLight"
                        (click)="pickType(option.type)"
                        (dblclick)="pickType(option.type); next()">
                        <span class="type-card__icon">
                            <ra-icon [icon]="option.icon" [varColor]="option.color" [size]="22" />
                        </span>
                        <span class="type-card__texts">
                            <strong>{{ option.labelKey | translate }}</strong>
                            <small>{{ option.hintKey | translate }}</small>
                        </span>
                        @if (type() === option.type) {
                            <span class="check" aria-hidden="true">
                                <ra-icon icon="tick" varColor="contentInverse" [size]="12" />
                            </span>
                        }
                    </button>
                }
            </div>
        }

        <!-- ───────────── Passo 2: modelo ───────────── -->
        @if (step() === 'template') {
            <div class="wizard__head wizard__head--row">
                <div>
                    <h3>{{ 'order.iaWizard.template.title' | translate }}</h3>
                    <p>{{ 'order.iaWizard.template.subtitle' | translate }}</p>
                </div>

                <div class="segmented" role="tablist">
                    <button
                        type="button"
                        role="tab"
                        [attr.aria-selected]="source() === 'rapidocs'"
                        [class.segmented__item--active]="source() === 'rapidocs'"
                        class="segmented__item"
                        (click)="pickSource('rapidocs')">
                        <ra-icon icon="magicpen" varColor="currentColor" [size]="14" />
                        {{ 'order.iaWizard.template.rapidocs' | translate }}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        [attr.aria-selected]="source() === 'mine'"
                        [class.segmented__item--active]="source() === 'mine'"
                        class="segmented__item"
                        (click)="pickSource('mine')">
                        <ra-icon icon="folder" varColor="currentColor" [size]="14" />
                        {{ 'order.iaWizard.template.mine' | translate }}
                    </button>
                </div>
            </div>

            <div
                class="gallery"
                role="radiogroup"
                [attr.aria-label]="'order.iaWizard.template.title' | translate"
                [style.--card-accent]="typeOption()?.accent"
                [style.--card-accent-light]="typeOption()?.accentLight">

                <!-- Em branco: sempre o primeiro, nas duas abas -->
                <button
                    type="button"
                    role="radio"
                    class="tpl-card"
                    [attr.aria-checked]="isChosen({ kind: 'blank' })"
                    [class.tpl-card--selected]="isChosen({ kind: 'blank' })"
                    (click)="pick({ kind: 'blank' })"
                    (dblclick)="pickAndContinue({ kind: 'blank' })">
                    <span class="tpl-card__preview tpl-card__preview--blank">
                        <template-thumbnail [blank]="true" />
                        <span class="tpl-card__plus" aria-hidden="true">
                            <ra-icon icon="add" varColor="contentSecondary" [size]="20" />
                        </span>
                        @if (isChosen({ kind: 'blank' })) {
                            <span class="check check--corner" aria-hidden="true">
                                <ra-icon icon="tick" varColor="contentInverse" [size]="12" />
                            </span>
                        }
                    </span>
                    <span class="tpl-card__texts">
                        <strong>{{ 'order.iaWizard.template.blank' | translate }}</strong>
                        <small>{{ 'order.iaWizard.template.blankHint' | translate }}</small>
                    </span>
                </button>

                @if (loadingTemplates()) {
                    @for (i of [1, 2, 3, 4, 5]; track i) {
                        <div class="tpl-card tpl-card--skeleton" aria-hidden="true">
                            <span class="tpl-card__preview"></span>
                            <span class="tpl-card__texts">
                                <span class="sk sk--title"></span>
                                <span class="sk sk--meta"></span>
                            </span>
                        </div>
                    }
                } @else {
                    @for (card of cards(); track card.key) {
                        <button
                            type="button"
                            role="radio"
                            class="tpl-card"
                            [attr.aria-checked]="isChosen(card.choice)"
                            [class.tpl-card--selected]="isChosen(card.choice)"
                            (click)="pick(card.choice)"
                            (dblclick)="pickAndContinue(card.choice)">
                            <span class="tpl-card__preview">
                                <template-thumbnail
                                    [preview]="card.preview"
                                    [sections]="card.sections"
                                    [quote]="type() === 'QUOTE'"
                                    [accent]="typeOption()?.accent ?? 'var(--primary)'"
                                    [accentLight]="typeOption()?.accentLight ?? 'var(--primaryLight)'" />
                                @if (isChosen(card.choice)) {
                                    <span class="check check--corner" aria-hidden="true">
                                        <ra-icon icon="tick" varColor="contentInverse" [size]="12" />
                                    </span>
                                }
                            </span>
                            <span class="tpl-card__texts">
                                <strong>{{ card.title }}</strong>
                                <span class="tpl-card__meta">
                                    <small>{{ card.meta }}</small>
                                    @if (card.featured) {
                                        <span class="tpl-card__badge">{{ 'order.iaWizard.template.featured' | translate }}</span>
                                    }
                                </span>
                            </span>
                        </button>
                    }
                }
            </div>

            @if (!loadingTemplates() && !cards().length) {
                <div class="gallery-empty">
                    @if (source() === 'mine') {
                        <strong>{{ 'order.iaWizard.template.emptyMine' | translate: { type: (typeOption()?.labelKey ?? '') | translate } }}</strong>
                        <p>{{ 'order.iaWizard.template.emptyMineHint' | translate }}</p>
                        <button type="button" class="link-button" (click)="pickSource('rapidocs')">
                            {{ 'order.iaWizard.template.seeRapidocs' | translate }}
                        </button>
                    } @else {
                        <strong>{{ 'order.iaWizard.template.emptyRapidocs' | translate }}</strong>
                    }
                </div>
            }
        }

        <!-- ───────────── Passo 3: contexto ───────────── -->
        @if (step() === 'context') {
            <div class="wizard__head">
                <h3>
                    {{ 'order.iaWizard.context.title' | translate }}
                    <span class="optional">{{ 'order.iaWizard.context.optional' | translate }}</span>
                </h3>
                <p>{{ 'order.iaWizard.context.subtitle' | translate }}</p>
            </div>

            <div class="context">
                <label class="context__label" for="ia-wizard-description">
                    {{ 'order.iaWizard.context.label' | translate }}
                </label>
                <textarea
                    id="ia-wizard-description"
                    rows="6"
                    [value]="description()"
                    [attr.maxlength]="descriptionMax"
                    [placeholder]="'order.iaWizard.context.placeholder' | translate"
                    (input)="onDescriptionInput($event)"></textarea>
                <div class="context__meta">
                    <span class="context__count">{{ description().length }}/{{ descriptionMax }}</span>
                </div>

                @if (suggestions().length) {
                    <p class="context__suggestions-label">{{ 'order.iaWizard.context.suggestionsLabel' | translate }}</p>
                    <div class="context__suggestions">
                        @for (suggestion of suggestions(); track suggestion) {
                            <button type="button" class="chip" (click)="useSuggestion(suggestion)">{{ suggestion }}</button>
                        }
                    </div>
                }
            </div>
        }

        <!-- Rodapé -->
        <div class="wizard__footer">
            @if (stepIndex() === 0) {
                <!-- O "+" da lista abre este wizard: os outros caminhos de criação ficam aqui -->
                <div class="wizard__shortcuts">
                    <button type="button" class="btn btn--ghost" (click)="newService()">
                        <ra-icon icon="folder" varColor="currentColor" [size]="16" />
                        {{ 'order.iaWizard.newService' | translate }}
                    </button>
                    @if (canImport()) {
                        <button type="button" class="btn btn--ghost" (click)="importFile()">
                            <ra-icon icon="document-upload" varColor="currentColor" [size]="16" />
                            {{ 'order.iaWizard.importFile' | translate }}
                        </button>
                    }
                </div>
            } @else {
                <button type="button" class="btn btn--ghost" [disabled]="creating()" (click)="back()">
                    <ra-icon icon="arrow-left" varColor="currentColor" [size]="16" />
                    {{ 'order.iaWizard.back' | translate }}
                </button>
            }

            <div class="wizard__footer-actions">
                @if (step() === 'context') {
                    <button type="button" class="btn btn--outline" [disabled]="creating()" (click)="skipContext()">
                        {{ 'order.iaWizard.skip' | translate }}
                    </button>
                    <button type="button" class="btn btn--primary" [disabled]="creating()" (click)="create()">
                        @if (creating()) {
                            <span class="spinner" aria-hidden="true"></span>
                            {{ 'order.iaWizard.creating' | translate }}
                        } @else {
                            <ra-icon icon="magicpen" varColor="currentColor" [size]="16" />
                            {{ 'order.iaWizard.createWithIa' | translate }}
                        }
                    </button>
                } @else if (isLastStep()) {
                    <button type="button" class="btn btn--primary" [disabled]="!canContinue() || creating()" (click)="next()">
                        @if (creating()) {
                            <span class="spinner" aria-hidden="true"></span>
                            {{ 'order.iaWizard.creating' | translate }}
                        } @else {
                            {{ 'order.iaWizard.createDocument' | translate }}
                        }
                    </button>
                } @else {
                    <button type="button" class="btn btn--primary" [disabled]="!canContinue()" (click)="next()">
                        {{ 'order.iaWizard.continue' | translate }}
                        <ra-icon icon="arrow-right" varColor="currentColor" [size]="16" />
                    </button>
                }
            </div>
        </div>
    </div>
</ra-modal>
